import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Logo } from "../components/Logo";
import { Calendar, User, AlertTriangle, FileText, Users, Home, Heart, Briefcase } from "lucide-react";
import { RenderStructuredValue } from "../formatProfil";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

const RUBRIQUE_LABELS = {
  identite: "Identité",
  famille: "Famille",
  couple: "Couple",
  documents_juridiques: "Documents juridiques",
  patrimoine_immobilier: "Patrimoine immobilier",
  patrimoine_financier: "Patrimoine financier",
  dettes: "Dettes",
  dernieres_volontes: "Dernières volontés",
  patrimoine_numerique: "Patrimoine numérique",
  sante: "Don d'organes",
  personnes_confiance: "Personnes de confiance",
};

const FIELD_LABELS = {
  nom_complet: "Nom complet", date_naissance: "Date de naissance",
  lieu_naissance: "Lieu de naissance", nationalite: "Nationalité",
  adresse_voie: "Adresse", adresse_code_postal: "Code postal", adresse_ville: "Ville",
  email_perso: "Email", telephone_perso: "Téléphone", profession: "Profession",
  statut: "Statut", regime_matrimonial: "Régime matrimonial",
  testament: "Testament", lieu_testament: "Lieu du testament",
  mandat_protection: "Mandat de protection future",
  assurance_vie: "Assurance-vie", credit_immo: "Crédit immobilier en cours",
  type_obseques: "Type d'obsèques", lieu_obseques: "Lieu",
  message_proches: "Message aux proches", don_organes: "Position don d'organes",
  position_don_organes: "Position personnelle", proches_informes: "Proches informés",
  note_personnelle: "Notes personnelles",
};

export default function HeritierView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/heritier/access/${token}`)
      .then((r) => setData(r.data))
      .catch((err) => setError(err?.response?.data?.detail || "Lien invalide ou expiré"))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "var(--auprea-cream)" }}>Chargement…</div>;
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ backgroundColor: "var(--auprea-cream)" }}>
        <div className="auprea-card max-w-md w-full">
          <AlertTriangle size={28} style={{ color: "#b53a3a", margin: "0 auto 14px", display: "block" }} />
          <h2 style={{ fontSize: 22, textAlign: "center", marginBottom: 10 }}>Lien invalide</h2>
          <p style={{ color: "rgba(35,38,46,0.75)", textAlign: "center", fontSize: 14 }}>{error}</p>
          <Link to="/" className="auprea-btn w-full inline-block text-center mt-4">Retour</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20" style={{ backgroundColor: "var(--auprea-cream)" }}>
      {/* Watermark fixe en haut */}
      <div style={{ background: "var(--auprea-navy)", color: "#f3efe6", padding: "12px 24px", textAlign: "center", fontSize: 13, letterSpacing: "0.06em" }} data-testid="heritier-banner">
        🔒 Accès héritier · Lecture seule · Lien personnel et confidentiel
      </div>

      <div className="px-6 md:px-12 lg:px-24 py-10 max-w-5xl mx-auto space-y-8">
        <div style={{ textAlign: "center" }}>
          <Logo size={32} />
        </div>

        <div className="auprea-card text-center" style={{ borderTop: "3px solid var(--auprea-gold)" }}>
          <div style={{ color: "var(--auprea-gold)", fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 600, marginBottom: 8 }}>
            — Carnet de
          </div>
          <h1 style={{ fontSize: 36, margin: "0 0 12px 0" }} data-testid="defunt-name">{data.defunt.nom_complet}</h1>
          <div style={{ fontSize: 14, color: "rgba(35,38,46,0.6)" }}>
            Décès le {new Date(data.defunt.date_deces).toLocaleDateString("fr-FR")}
          </div>
          <div style={{ fontSize: 12, color: "rgba(35,38,46,0.5)", marginTop: 18, lineHeight: 1.6 }}>
            Accès accordé à {data.demandeur.prenom} {data.demandeur.nom} ({data.demandeur.lien.replace(/_/g, " ")}).
            <br/>Lien valable jusqu'au {new Date(data.access_expires_at).toLocaleDateString("fr-FR")}.
          </div>
        </div>

        {/* Rubriques */}
        {Object.entries(data.profil || {}).map(([rubrique, fields]) => {
          if (Object.keys(fields).length === 0) return null;
          return (
            <div key={rubrique} className="auprea-card" data-testid={`section-${rubrique}`}>
              <h2 style={{ fontSize: 22, marginBottom: 16, display: "flex", alignItems: "center", gap: 10 }}>
                <FileText size={18} style={{ color: "var(--auprea-gold)" }} />
                {RUBRIQUE_LABELS[rubrique] || rubrique}
              </h2>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
                {Object.entries(fields).map(([k, v]) => v && (
                  <div key={k}>
                    <dt style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.1em", color: "rgba(35,38,46,0.5)", fontWeight: 600, marginBottom: 3 }}>
                      {FIELD_LABELS[k] || k.replace(/_/g, " ")}
                    </dt>
                    <dd style={{ fontSize: 14, color: "var(--auprea-ink)", margin: 0, whiteSpace: "pre-wrap" }}>
                      <RenderStructuredValue value={v} />
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          );
        })}

        {/* Enfants */}
        {data.enfants && data.enfants.length > 0 && (
          <div className="auprea-card">
            <h2 style={{ fontSize: 22, marginBottom: 16, display: "flex", alignItems: "center", gap: 10 }}>
              <Users size={18} style={{ color: "var(--auprea-gold)" }} /> Enfants
            </h2>
            <ul style={{ fontSize: 14, lineHeight: 1.8 }}>
              {data.enfants.map((e, i) => (
                <li key={i}>• {e.prenom} {e.nom} {e.date_naissance && `(né(e) le ${new Date(e.date_naissance).toLocaleDateString("fr-FR")})`}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Biens */}
        {data.biens && data.biens.length > 0 && (
          <div className="auprea-card">
            <h2 style={{ fontSize: 22, marginBottom: 16, display: "flex", alignItems: "center", gap: 10 }}>
              <Home size={18} style={{ color: "var(--auprea-gold)" }} /> Biens immobiliers
            </h2>
            <ul style={{ fontSize: 14, lineHeight: 1.8 }}>
              {data.biens.map((b, i) => (
                <li key={i}>• {b.type || "Bien"} — {b.adresse || ""} {b.ville && `(${b.ville})`}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Personnes de confiance */}
        {data.personnes_confiance && data.personnes_confiance.length > 0 && (
          <div className="auprea-card">
            <h2 style={{ fontSize: 22, marginBottom: 16, display: "flex", alignItems: "center", gap: 10 }}>
              <Heart size={18} style={{ color: "var(--auprea-gold)" }} /> Personnes de confiance
            </h2>
            <ul style={{ fontSize: 14, lineHeight: 1.8 }}>
              {data.personnes_confiance.map((p, i) => (
                <li key={i}>• {p.prenom} {p.nom} {p.role && `— ${p.role}`} {p.email && `· ${p.email}`}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Documents */}
        {data.documents && data.documents.length > 0 && (
          <div className="auprea-card">
            <h2 style={{ fontSize: 22, marginBottom: 16, display: "flex", alignItems: "center", gap: 10 }}>
              <Briefcase size={18} style={{ color: "var(--auprea-gold)" }} /> Documents
            </h2>
            <p style={{ fontSize: 13, color: "rgba(35,38,46,0.6)", marginBottom: 12 }}>
              {data.documents.length} document(s) joint(s) au carnet. Pour leur consultation,
              veuillez contacter <a href="mailto:contact@auprea.com" style={{ color: "var(--auprea-blue)" }}>contact@auprea.com</a>.
            </p>
            <ul style={{ fontSize: 14, lineHeight: 1.8 }}>
              {data.documents.map((d, i) => (
                <li key={i}>📄 {d.nom}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="auprea-card" style={{ background: "rgba(202,168,106,0.06)", textAlign: "center" }}>
          <p style={{ fontSize: 13, color: "rgba(35,38,46,0.7)", lineHeight: 1.6, margin: 0 }}>
            Ce carnet est strictement personnel et a été partagé avec vous en tant qu'héritier.
            Pour toute question : <a href="mailto:contact@auprea.com" style={{ color: "var(--auprea-blue)" }}>contact@auprea.com</a>
          </p>
        </div>
      </div>
    </div>
  );
}
